/**
 * Customer message templates.
 *
 * Deliberately a tiny renderer rather than a real template engine: these strings
 * are edited by shop staff in a textarea and sent to customers, so the language
 * has to be guessable from looking at an example, and an unknown placeholder must
 * degrade to something harmless rather than throwing on the way to a customer.
 */
const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;
function lookup(context, path) {
    const parts = path.split('.');
    let current = context;
    for (const part of parts) {
        if (current === null || current === undefined || typeof current !== 'object')
            return null;
        current = current[part];
    }
    if (current === null || current === undefined)
        return null;
    return String(current);
}
/** Unresolved placeholders are dropped, not left as literal `{{...}}` braces. */
export function renderTemplate(template, context) {
    return template
        .replace(PLACEHOLDER, (_match, path) => lookup(context, path) ?? '')
        // Collapse the blank lines and stray spaces a dropped placeholder leaves behind.
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
export const TEMPLATE_VARIABLES = [
    { path: 'customer.firstName', label: 'Customer first name', example: 'Maria' },
    { path: 'customer.displayName', label: 'Customer full name', example: 'Maria Santos' },
    { path: 'customer.phone', label: 'Customer phone', example: '0917 123 4567' },
    { path: 'order.number', label: 'Order number', example: 'AB-000142' },
    { path: 'order.total', label: 'Order total', example: '₱1,250.00' },
    { path: 'order.balanceDue', label: 'Balance still owed', example: '₱1,250.00' },
    { path: 'order.amountPaid', label: 'Amount already paid', example: '₱0.00' },
    { path: 'order.itemLines', label: 'Itemised list', example: '2x Focaccia — ₱700\n1x Pastry box — ₱550' },
    { path: 'order.itemCount', label: 'Number of items', example: '3' },
    { path: 'order.dueDate', label: 'Ready / delivery date', example: 'Sat, 30 Aug' },
    {
        path: 'order.dueTime',
        label: 'Pick-up time, or the delivery window',
        example: '3:00 PM — or “5:00 – 7:00 PM” for a delivery',
    },
    { path: 'order.fulfillment', label: 'Pickup or delivery', example: 'Pickup' },
    { path: 'order.address', label: 'Delivery address', example: '12 Rizal St, Taguig' },
    { path: 'order.notes', label: 'Order notes', example: 'No sesame please' },
    { path: 'order.rider', label: 'Rider name', example: 'Danielle' },
    { path: 'shop.name', label: 'Shop name', example: "ANA's BAKERY" },
    { path: 'shop.phone', label: 'Shop phone', example: '0960 857 4458' },
    { path: 'shop.address', label: 'Shop address', example: 'Unit 4 MMV Bldg, 126 M.L. Quezon St, Taguig' },
    { path: 'payment.instructions', label: 'Payment instructions', example: 'GCash 0960 857 4458 (ANA & CO.)' },
];
/**
 * Shipped defaults for the four statuses in the brief. Every one of these is
 * editable, and statuses added later start from a blank template.
 */
export const DEFAULT_TEMPLATES = [
    {
        key: 'order_new',
        name: 'New order — summary and payment details',
        body: `Hi {{customer.firstName}}! Thank you for ordering from {{shop.name}} 🥖

Order {{order.number}}
{{order.itemLines}}

Total: {{order.total}}
For: {{order.fulfillment}} on {{order.dueDate}}, {{order.dueTime}}

To confirm your order, please settle {{order.balanceDue}}:
{{payment.instructions}}

Send us a screenshot of your payment and we will confirm right away. Salamat po!`,
    },
    {
        key: 'order_paid',
        name: 'Paid — payment confirmed',
        body: `Payment received, {{customer.firstName}} — salamat po! ✅

Order {{order.number}} is confirmed.
Amount paid: {{order.amountPaid}}

We will have it ready for {{order.fulfillment}} on {{order.dueDate}} at {{order.dueTime}}.

Any changes, just message us here.`,
    },
    {
        key: 'order_ready_for_pickup',
        name: 'Ready for pick-up — with instructions',
        body: `Your order is ready, {{customer.firstName}}! 🎉

Order {{order.number}} is packed and waiting for you.

Pick-up address:
{{shop.address}}

Please look for the counter and give your order number {{order.number}}.
Questions on the way? Call or text {{shop.phone}}.

Freshly baked — best enjoyed today!`,
    },
    {
        key: 'order_dispatched',
        name: 'Dispatched — on the way',
        body: `On the way, {{customer.firstName}}! 🛵

Order {{order.number}} has left the bakery and is heading to:
{{order.address}}

Please keep your phone nearby — our rider {{order.rider}} may call.
Balance to settle on delivery: {{order.balanceDue}}

Shop contact: {{shop.phone}}`,
    },
];
/** Everything after a placeholder is resolved, ready to hand to a channel adapter. */
export function previewTemplate(body) {
    const context = {};
    for (const variable of TEMPLATE_VARIABLES) {
        const parts = variable.path.split('.');
        let cursor = context;
        parts.forEach((part, index) => {
            if (index === parts.length - 1) {
                cursor[part] = variable.example;
            }
            else {
                cursor[part] = cursor[part] ?? {};
                cursor = cursor[part];
            }
        });
    }
    return renderTemplate(body, context);
}
//# sourceMappingURL=templates.js.map